import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Radio, Play, Pause, SkipBack, SkipForward, Monitor, Award, Users,
  ArrowLeft, Music2, RotateCcw, Trophy, Coffee, Loader2, PowerOff,
  ChevronLeft, ChevronRight,
} from 'lucide-react'
import { visionAPI } from '../lib/api'
import { useVisionSocket } from '../lib/visionSocket'
import { Button } from '../components/ui/button'

/* Touch-first regie voor Kempenvision — grote knoppen, sticky header met
   huidige stage-view én phase, minimaal tekst. Gebruikt dezelfde updateState/
   updateShow endpoints als de desktop control-pagina; MIDI en Streamdeck
   blijven parallel werken. Portrait op gsm, landscape op tablet. Live-sync
   via websocket. */

const VIEWS = [
  { key: 'idle',     label: 'Idle',     icon: PowerOff, tone: 'bg-slate-700  border-slate-500' },
  { key: 'song',     label: 'Song',     icon: Music2,   tone: 'bg-pink-700   border-pink-500' },
  { key: 'ceremony', label: 'Ceremony', icon: Award,    tone: 'bg-yellow-700 border-yellow-500' },
  { key: 'results',  label: 'Results',  icon: Trophy,   tone: 'bg-emerald-700 border-emerald-500' },
]
const PHASES = [
  { key: 'setup',         label: 'Setup' },
  { key: 'live_songs',    label: 'Live songs' },
  { key: 'pauze',         label: 'Pauze' },
  { key: 'jury_reveal',   label: 'Jury reveal' },
  { key: 'public_vote',   label: 'Publiek stemt' },
  { key: 'public_reveal', label: 'Publiek reveal' },
  { key: 'finished',      label: 'Klaar' },
]

export default function KempenvisionTouchPage() {
  const [data, setData] = useState(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  const load = async () => {
    try { const { data } = await visionAPI.getControlData(); setData(data); setErr(null) }
    catch (e) { setErr(e.message) }
  }
  useEffect(() => { load(); const iv = setInterval(load, 2000); return () => clearInterval(iv) }, [])
  useVisionSocket(load)

  const patchState = async (u) => { setBusy(true); try { await visionAPI.updateState(u); await load() } finally { setBusy(false) } }
  const patchShow  = async (u) => { setBusy(true); try { await visionAPI.updateShow(data.show.id, u); await load() } finally { setBusy(false) } }

  if (!data?.show) {
    return (
      <div className="min-h-screen grid place-items-center bg-black text-white p-6">
        <div className="text-center">
          <Radio className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
          <p className="text-muted-foreground">Geen actieve show.</p>
          <Button asChild variant="outline" className="mt-4"><Link to="/kempenvision/admin">Naar admin</Link></Button>
        </div>
      </div>
    )
  }

  const { show, state, entries, juries, current_jury_votes, standings } = data
  const view = state.current_view || 'idle'
  const activeMeta = VIEWS.find(v => v.key === view) || VIEWS[0]
  const currentEntry = entries.find(e => e.id === state.current_entry_id)
  const currentJury  = juries.find(j => j.id === state.current_jury_id)

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      {/* Sticky header */}
      <div className="sticky top-0 z-20 bg-black/95 backdrop-blur border-b border-white/10 px-4 py-3">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="sm" className="text-white hover:bg-white/10">
            <Link to="/kempenvision/control"><ArrowLeft className="h-4 w-4" /></Link>
          </Button>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-white/50 truncate">{show.name}{show.edition ? ` · ${show.edition}` : ''}</div>
            <div className="text-lg font-bold flex items-center gap-2">
              <activeMeta.icon className="h-5 w-5" />
              {activeMeta.label}
              <span className="text-[10px] uppercase tracking-widest text-white/40">
                · fase: {PHASES.find(p => p.key === show.phase)?.label || show.phase}
              </span>
              {busy && <Loader2 className="h-4 w-4 animate-spin text-white/50" />}
            </div>
          </div>
          <Button asChild variant="ghost" size="sm" className="text-white hover:bg-white/10">
            <a href="/kempenvision/stage" target="_blank" rel="noopener"><Monitor className="h-4 w-4" /></a>
          </Button>
        </div>
        {err && <div className="mt-2 text-xs text-red-400">⚠ {err}</div>}
      </div>

      <div className="flex-1 p-4 space-y-4 max-w-3xl w-full mx-auto">
        {/* View-picker */}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2 px-1">Stage-weergave</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {VIEWS.map(v => {
              const Icon = v.icon
              const on = view === v.key
              return (
                <button key={v.key} disabled={busy}
                        onClick={() => patchState({ current_view: v.key })}
                        className={`h-24 rounded-lg border-2 flex flex-col items-center justify-center gap-1 transition-transform active:scale-95 ${
                          on ? `${v.tone} text-white shadow-lg` : 'border-white/15 bg-white/5 text-white/70'}`}>
                  <Icon className="h-7 w-7" />
                  <span className="text-xs font-semibold uppercase tracking-widest">{v.label}</span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Song controls */}
        {view === 'song' && (
          <SongControls entries={entries} current={currentEntry} state={state}
                        patchState={patchState} busy={busy} />
        )}

        {/* Ceremony controls */}
        {view === 'ceremony' && (
          <CeremonyControls juries={juries} currentJury={currentJury} state={state}
                            current_jury_votes={current_jury_votes}
                            patchState={patchState} busy={busy} />
        )}

        {/* Winnaar-nummer play/pauze — alleen zichtbaar in results-view én er
            is een winnaar met audio. Zelfde audio_playing-flag als song-view,
            en `current_entry_id` op de winnaar zetten zodat stage weet welk
            bestand te spelen. Vereist dat op stage al éénmaal geklikt is voor
            de Chrome autoplay-unlock (zie initAudio in StagePage). */}
        {view === 'results' && (standings || []).length > 0 && (() => {
          const winner = entries.find(e => e.id === standings[0].entry_id)
          if (!winner?.audio_url) {
            return (
              <div className="rounded-lg border-2 border-yellow-500/40 bg-yellow-500/10 p-4 text-yellow-300 text-sm text-center">
                🎵 Winnaar heeft geen audio-bestand; play niet beschikbaar.
              </div>
            )
          }
          const speelt = state.audio_playing && state.current_entry_id === winner.id
          return (
            <div className="rounded-lg border-2 border-yellow-500/60 bg-yellow-500/10 p-4 space-y-3">
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-widest text-yellow-300">Winnaar</div>
                <div className="text-2xl font-bold mt-1 truncate">{winner.title}</div>
                <div className="text-sm text-white/70 truncate">{standings[0].team_name}</div>
              </div>
              <button disabled={busy}
                      onClick={() => patchState({ audio_playing: !speelt, current_entry_id: winner.id })}
                      className={`w-full h-20 rounded-lg border-2 flex items-center justify-center gap-3 active:scale-95 transition-transform disabled:opacity-30 disabled:pointer-events-none ${
                        speelt
                          ? 'border-red-500 bg-red-500/20 text-red-300'
                          : 'border-yellow-500 bg-yellow-500/20 text-yellow-200'}`}>
                {speelt ? <Pause className="h-8 w-8" /> : <Play className="h-8 w-8" />}
                <span className="text-lg font-bold uppercase">
                  {speelt ? 'Pauzeer winnaar' : 'Speel winnaar-nummer'}
                </span>
              </button>
            </div>
          )
        })()}

        {/* Publiek-flow navigator — na jury-reveal wil de regisseur snel naar
            publiek-stemmen en dan naar publiek-reveal. In plaats van dat via de
            kleine phase-picker onderaan te moeten doen, staat hier een grote
            knop met de volgende logische stap. */}
        {show.phase === 'jury_reveal' && (
          <button disabled={busy}
                  onClick={() => patchShow({ phase: 'public_vote', vote_open: true })}
                  className="w-full h-20 rounded-lg border-2 border-cyan-500 bg-cyan-500/15 text-cyan-200 font-bold uppercase tracking-widest active:scale-95 transition-transform">
            → Open publiek-stemmen
          </button>
        )}
        {show.phase === 'public_vote' && (
          <button disabled={busy}
                  onClick={() => patchShow({ phase: 'public_reveal', vote_open: false })}
                  className="w-full h-20 rounded-lg border-2 border-cyan-500 bg-cyan-500/15 text-cyan-200 font-bold uppercase tracking-widest active:scale-95 transition-transform">
            → Start publiek reveal
          </button>
        )}

        {/* Publiek-reveal controls — apart van view-picker; verschijnt zodra
            de show-fase op public_reveal staat, ongeacht huidige view. */}
        {show.phase === 'public_reveal' && (
          <PublicRevealControls state={state} entries={entries}
                                publicRevealOrder={data.public_reveal_order || []}
                                currentReveal={data.current_public_reveal}
                                patchState={patchState} busy={busy} />
        )}

        {/* Phase picker — altijd bereikbaar onderaan */}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2 px-1">Fase</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {PHASES.map(p => {
              const on = show.phase === p.key
              return (
                <button key={p.key} disabled={busy}
                        onClick={() => patchShow({ phase: p.key })}
                        className={`h-14 rounded-lg border-2 text-sm font-semibold uppercase tracking-widest active:scale-95 transition-transform ${
                          on ? 'border-acid bg-acid/20 text-acid' : 'border-white/15 bg-white/5 text-white/70'}`}>
                  {p.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Blackout */}
        <button onClick={() => patchState({ current_view: 'idle' })} disabled={busy}
                className="w-full h-16 rounded-lg border-2 border-red-500/40 bg-red-500/10 text-red-300 font-bold uppercase tracking-widest active:scale-95 transition-transform">
          Blackout · terug naar idle
        </button>
      </div>
    </div>
  )
}

function SongControls({ entries, current, state, patchState, busy }) {
  const idx = entries.findIndex(e => e.id === current?.id)
  const prev = idx > 0 ? entries[idx - 1] : null
  const next = idx >= 0 && idx < entries.length - 1 ? entries[idx + 1] : (idx === -1 ? entries[0] : null)
  return (
    <div className="space-y-3">
      {/* Huidig blok */}
      <div className="rounded-lg border border-white/10 bg-white/5 p-4 text-center">
        <div className="text-[10px] uppercase tracking-widest text-white/50">Nu op stage</div>
        {current ? (
          <>
            <div className="text-2xl font-bold mt-1 truncate">{current.title}</div>
            <div className="text-sm text-white/70 truncate">{current.team_name}</div>
            <div className="text-[10px] text-white/40 mt-1">#{current.running_order}</div>
          </>
        ) : <div className="text-white/50 italic mt-1">Nog niks geselecteerd</div>}
      </div>

      {/* Audio play/pause */}
      <button disabled={busy || !current}
              onClick={() => patchState({ audio_playing: !state.audio_playing })}
              className={`w-full h-20 rounded-lg border-2 flex items-center justify-center gap-3 active:scale-95 transition-transform disabled:opacity-30 disabled:pointer-events-none ${
                state.audio_playing
                  ? 'border-red-500 bg-red-500/20 text-red-300'
                  : 'border-emerald-500 bg-emerald-500/20 text-emerald-300'}`}>
        {state.audio_playing ? <Pause className="h-8 w-8" /> : <Play className="h-8 w-8" />}
        <span className="text-lg font-bold uppercase">{state.audio_playing ? 'Pauze' : 'Play'}</span>
      </button>

      {/* Prev/next */}
      <div className="grid grid-cols-2 gap-3">
        <button disabled={busy || !prev}
                onClick={() => prev && patchState({ current_entry_id: prev.id, audio_playing: false })}
                className="h-16 rounded-lg border-2 border-white/15 bg-white/5 disabled:opacity-30 disabled:pointer-events-none flex items-center justify-center gap-2 active:scale-95 transition-transform">
          <SkipBack className="h-6 w-6" />
          <span className="font-semibold">Vorige</span>
        </button>
        <button disabled={busy || !next}
                onClick={() => next && patchState({ current_entry_id: next.id, audio_playing: false })}
                className="h-16 rounded-lg border-2 border-acid bg-acid/10 text-acid disabled:opacity-30 disabled:pointer-events-none flex items-center justify-center gap-2 active:scale-95 transition-transform">
          <span className="font-semibold">Volgende</span>
          <SkipForward className="h-6 w-6" />
        </button>
      </div>

      {/* Direct pickers */}
      <div className="grid grid-cols-4 gap-2">
        {entries.map(e => (
          <button key={e.id} disabled={busy}
                  onClick={() => patchState({ current_entry_id: e.id, audio_playing: false })}
                  className={`h-14 rounded-lg border-2 text-xs font-semibold uppercase active:scale-95 transition-transform ${
                    e.id === current?.id ? 'border-acid bg-acid/20 text-acid' : 'border-white/15 bg-white/5 text-white/70'}`}>
            #{e.running_order}
          </button>
        ))}
      </div>
    </div>
  )
}

function CeremonyControls({ juries, currentJury, state, current_jury_votes, patchState, busy }) {
  // Ceremony-transport werkt per-jury. Aantal stappen = aantal stemmen die
  // deze jury ingediend heeft (backend levert die in current_jury_votes,
  // gesorteerd van laagste naar hoogste punten). Nooit meer dan dat.
  const step = state.ceremony_step ?? 0
  const votes = current_jury_votes || []
  const totalSteps = votes.length
  const nextVote    = step < totalSteps ? votes[step] : null
  const currentVote = step > 0 ? votes[step - 1] : null
  return (
    <div className="space-y-3">
      {/* Jury picker */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2 px-1">Jury</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {juries.map(j => {
            const on = j.id === currentJury?.id
            return (
              <button key={j.id} disabled={busy}
                      onClick={() => patchState({ current_jury_id: j.id, ceremony_step: 0 })}
                      className={`h-16 rounded-lg border-2 flex items-center justify-center text-sm font-semibold active:scale-95 transition-transform ${
                        on ? 'border-yellow-400 bg-yellow-400/15 text-yellow-200' : 'border-white/15 bg-white/5 text-white/70'}`}>
                {j.name}{j.is_locked ? ' ✓' : ''}
              </button>
            )
          })}
        </div>
      </div>

      {currentJury && !currentJury.is_locked && (
        <div className="rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-3 text-yellow-300 text-sm">
          ⚠ Deze jury heeft haar stemmen nog niet definitief ingediend.
        </div>
      )}

      {/* Points progress */}
      {currentJury && (
        <div className="rounded-lg border border-yellow-500/40 bg-yellow-500/5 p-4 space-y-3">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/50">Getoond</div>
              <div className="text-3xl font-bold text-cyan-300 tabular-nums">{currentVote ? currentVote.points : '—'}</div>
              <div className="text-xs text-white/60 truncate">{currentVote?.team_name || 'nog niks'}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/50">Progress</div>
              <div className="text-3xl font-bold tabular-nums">{step}/{totalSteps}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/50">Volgende</div>
              <div className="text-3xl font-bold text-pink-400 tabular-nums">{nextVote ? nextVote.points : '—'}</div>
              <div className="text-xs text-white/60 truncate">{nextVote?.team_name || (totalSteps > 0 ? 'klaar' : '—')}</div>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-2">
            <button disabled={busy || step === 0}
                    onClick={() => patchState({ ceremony_step: 0 })}
                    className="h-16 rounded-lg border-2 border-white/15 bg-white/5 disabled:opacity-30 disabled:pointer-events-none flex items-center justify-center active:scale-95 transition-transform">
              <RotateCcw className="h-5 w-5" />
            </button>
            <button disabled={busy || step === 0}
                    onClick={() => patchState({ ceremony_step: Math.max(0, step - 1) })}
                    className="h-16 rounded-lg border-2 border-white/15 bg-white/5 disabled:opacity-30 disabled:pointer-events-none flex items-center justify-center active:scale-95 transition-transform">
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button disabled={busy || !nextVote}
                    onClick={() => patchState({ ceremony_step: Math.min(totalSteps, step + 1) })}
                    className="col-span-2 h-16 rounded-lg border-2 border-yellow-500 bg-yellow-500/20 text-yellow-300 font-bold uppercase text-sm active:scale-95 transition-transform disabled:opacity-30 disabled:pointer-events-none">
              {nextVote ? `Reveal ${nextVote.points}pt → ${nextVote.team_name}` : 'Klaar'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function PublicRevealControls({ state, entries, publicRevealOrder, currentReveal, patchState, busy }) {
  // Publiek-reveal loopt over publicRevealOrder (backend zet dat op = aantal
  // deelnemers). Nooit meer stappen dan dat.
  const step = state.public_reveal_step ?? 0
  const order = publicRevealOrder || []
  const total = order.length || entries.length
  const nextId = order[step]
  const nextEntry = nextId ? entries.find(e => e.id === nextId) : null
  return (
    <div className="rounded-lg border border-cyan-500/40 bg-cyan-500/5 p-4 space-y-3">
      <div className="text-[10px] uppercase tracking-widest text-cyan-300 mb-1">Publiek reveal</div>
      <div className="grid grid-cols-3 gap-3 text-center">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/50">Getoond</div>
          <div className="text-3xl font-bold text-cyan-300 tabular-nums">{currentReveal ? currentReveal.points : '—'}</div>
          <div className="text-xs text-white/60 truncate">{currentReveal?.team_name || 'nog niks'}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/50">Progress</div>
          <div className="text-3xl font-bold tabular-nums">{step}/{total}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/50">Volgende</div>
          <div className="text-2xl font-bold text-pink-400">?</div>
          <div className="text-xs text-white/60 truncate">{nextEntry ? nextEntry.team_name : (step >= total ? 'klaar' : '—')}</div>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2">
        <button disabled={busy || step === 0}
                onClick={() => patchState({ public_reveal_step: 0 })}
                className="h-16 rounded-lg border-2 border-white/15 bg-white/5 disabled:opacity-30 disabled:pointer-events-none flex items-center justify-center active:scale-95 transition-transform">
          <RotateCcw className="h-5 w-5" />
        </button>
        <button disabled={busy || step === 0}
                onClick={() => patchState({ public_reveal_step: Math.max(0, step - 1) })}
                className="h-16 rounded-lg border-2 border-white/15 bg-white/5 disabled:opacity-30 disabled:pointer-events-none flex items-center justify-center active:scale-95 transition-transform">
          <ChevronLeft className="h-6 w-6" />
        </button>
        <button disabled={busy || step >= total}
                onClick={() => patchState({ public_reveal_step: Math.min(total, step + 1) })}
                className="col-span-2 h-16 rounded-lg border-2 border-cyan-500 bg-cyan-500/20 text-cyan-300 font-bold uppercase active:scale-95 transition-transform disabled:opacity-30 disabled:pointer-events-none">
          Reveal
        </button>
      </div>
    </div>
  )
}

/* Hold-to-play — voorkomt dat één per-ongeluk-tap tijdens de show de
   winnaar-audio start. Vinger 1 sec op de knop houden triggert de actie;
   vroegtijdig loslaten = niks. Werkt op touch (Pointer Events). */
function HoldToPlayButton({ onFire, disabled, label = 'Houd vast om te starten', duration = 1000 }) {
  const [progress, setProgress] = useState(0)
  const startedRef = useRef(0)
  const rafRef = useRef(null)
  const cancel = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = null
    startedRef.current = 0
    setProgress(0)
  }
  const tick = () => {
    const p = Math.min(1, (Date.now() - startedRef.current) / duration)
    setProgress(p)
    if (p >= 1) { cancel(); onFire?.() }
    else rafRef.current = requestAnimationFrame(tick)
  }
  const start = (e) => {
    if (disabled || startedRef.current) return
    e.preventDefault?.()
    startedRef.current = Date.now()
    rafRef.current = requestAnimationFrame(tick)
  }
  useEffect(() => () => cancel(), [])
  return (
    <button type="button" disabled={disabled}
            onPointerDown={start}
            onPointerUp={cancel}
            onPointerLeave={cancel}
            onPointerCancel={cancel}
            className="relative w-full h-20 rounded-lg border-2 border-yellow-500 bg-yellow-500/10 text-yellow-200 font-bold uppercase tracking-widest overflow-hidden select-none disabled:opacity-30 disabled:pointer-events-none">
      <div className="absolute inset-y-0 left-0 bg-yellow-500/40 transition-none"
           style={{ width: `${progress * 100}%` }} />
      <div className="relative flex items-center justify-center gap-3">
        <Play className="h-8 w-8" />
        <span className="text-lg">
          {progress > 0 ? `Doorgaan… ${Math.round(progress * 100)}%` : label}
        </span>
      </div>
    </button>
  )
}
